import { Button, Dialog, Input, Typography, IconButton, Checkbox, Textarea, Switch } from "@material-tailwind/react";
import { HiX, HiPlus, HiSave, HiTag, HiPencil, HiInformationCircle, HiOutlineExclamationCircle } from "react-icons/hi";
import { useEffect, useState, useRef } from "react";
import TradenameAPI from '../../api/tradename';
import IngredientAPI from '../../api/ingredient';
import ManufacturerAPI from '../../api/manufacturer';
import { toast } from 'react-toastify';
import { generateUri, validateUri } from '../../utils/uri';
import { useDialog } from '../../context/DialogContext';
import QuickSearchAndResult from '../organisms/QuickSearchAndResult';
import HelperInput from '../atoms/HelperInput';
// Custom Spinner component
const Spinner = ({ size = 5, className = "" }) => (
  <div className={`inline-block size-${size} animate-spin rounded-full border-2 border-solid border-current border-e-transparent align-[-0.125em] text-blue-500 ${className}`}></div>
);

const TradenameDialog = ({
  isOpen,
  onClose,
  onSuccess,
  mode = "create",
  tradenameUri,
  initialIngredients = [],
  initialManufacturers = []
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tradename, setTradename] = useState({
    name: '',
    uri: '',
    ingredients: [],
    manufacturers: [],
    description: '',
    rank: null,
    is_sponsor: false,
    review_score: null,
    purchase_link: '',
    categories: '',
    codes: '',
    status: 'inactive',
  });

  // Use refs to track previous props
  const previousProps = useRef({ isOpen, mode, tradenameUri });

  // Get dialog functions from context
  const { openDialog, setDialogProps, openIngredientDialog, openManufacturerDialog } = useDialog();

  // Add these state variables at the beginning of the component
  const [ingredientSearchKeyword, setIngredientSearchKeyword] = useState('');
  const [ingredientSearchResults, setIngredientSearchResults] = useState([]);
  const [manufacturerSearchKeyword, setManufacturerSearchKeyword] = useState('');
  const [manufacturerSearchResults, setManufacturerSearchResults] = useState([]);

  // Add pagination state for ingredients
  const [ingredientPagination, setIngredientPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0
  });

  // Add pagination state for manufacturers
  const [manufacturerPagination, setManufacturerPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0
  });

  // Reset form when dialog opens in create mode
  useEffect(() => {
    // Only run if isOpen or mode changed
    if (previousProps.current.isOpen !== isOpen ||
      previousProps.current.mode !== mode ||
      (mode === "edit" && previousProps.current.tradenameUri !== tradenameUri)) {

      // Update refs
      previousProps.current = { isOpen, mode, tradenameUri };

      // Only proceed if the dialog is open
      if (isOpen) {
        if (mode === "edit" && tradenameUri) {
          fetchTradename(tradenameUri);
        } else if (mode === "create") {
          // Reset form when opening in create mode
          setTradename({
            name: '',
            uri: '',
            ingredients: initialIngredients || [],
            manufacturers: initialManufacturers || [],
            description: '',
            rank: null,
            is_sponsor: false,
            review_score: null,
            purchase_link: '',
            categories: '',
            codes: '',
            rank: 100,
            status: 'inactive',
          });
        }
      }

      // Reset states when dialog closes
      if (!isOpen) {
        setSubmitting(false);
      }
    }
  }, [isOpen, mode, tradenameUri, initialIngredients, initialManufacturers]);

  const fetchTradename = async (uri) => {
    if (!uri) return;

    try {
      setLoading(true);
      const response = await TradenameAPI.getOne(uri);

      // Get manufacturers specific to this tradename if in edit mode
      let manufacturers = [];
      if (mode === 'edit') {
        try {
          const manufacturersResponse = await TradenameAPI.getManufacturersByUri(uri);
          if (manufacturersResponse && Array.isArray(manufacturersResponse.manufacturers)) {
            manufacturers = manufacturersResponse.manufacturers.map(man => ({
              _id: man._id,
              name: man.name,
              uri: man.uri
            }));
          }
        } catch (manufacturerError) {
          console.error('Error fetching related manufacturers:', manufacturerError);
        }
      }

      // Format ingredients and manufacturers to ensure they have proper objects
      const formattedIngredients = Array.isArray(response.ingredients)
        ? response.ingredients.map(ing => {
          if (typeof ing === 'object' && ing.name && ing._id && ing.uri) {
            return ing;
          }
          const id = typeof ing === 'object' ? ing._id : ing;
          const name = typeof ing === 'object' ? ing.name : `Ingredient ${id}`;
          const uri = typeof ing === 'object' ? ing.uri : '';
          return { _id: id, name, uri };
        })
        : [];

      const formattedManufacturers = manufacturers.length > 0
        ? manufacturers
        : Array.isArray(response.manufacturers)
          ? response.manufacturers.map(man => {
            if (typeof man === 'object' && man.name && man._id && man.uri) {
              return man;
            }
            const id = typeof man === 'object' ? man._id : man;
            const name = typeof man === 'object' ? man.name : `Manufacturer ${id}`;
            const uri = typeof man === 'object' ? man.uri : '';
            return { _id: id, name, uri };
          })
          : [];

      console.log(`Loaded ${formattedIngredients.length} ingredients and ${formattedManufacturers.length} manufacturers for tradename ${response.name}`);

      // handle codes
      let codesStr = '';
      if (response.codes && response.codes.length > 0) {
        codesStr = response.codes.map(code =>
          `${code.type}: ${code.values.length > 0 ? code.values.join(', ') : code.value}`
        ).join('\n');
      }

      // Update the state with formatted data
      setTradename({
        ...response,
        ingredients: formattedIngredients,
        manufacturers: formattedManufacturers,
        codes: codesStr
      });
    } catch (error) {
      console.error('Error fetching tradename:', error);
      toast.error('Failed to fetch tradename details');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!tradename.name || !tradename.uri || !validateUri(tradename.uri)) {
      toast.error('Please fill in all required fields correctly');
      return;
    }

    try {
      setSubmitting(true);

      
      // Parse the codes string into the expected format
      const parsedCodes = [];
      if (tradename.codes) {
        const codeLines = tradename.codes.split('\n');
        codeLines.forEach(line => {
          if (line.trim()) {
            const [type, valuesStr] = line.split(':');
            if (type && valuesStr) {
              const values = valuesStr.split(',').map(v => v.trim()).filter(v => v);
              if (values.length > 0) {
                parsedCodes.push({
                  type: type.trim(),
                  values
                });
              }
            }
          }
        });
      }
      

      // Format the data for API
      const tradenameData = {
        ...tradename,
        ingredients: tradename.ingredients.map(ing => typeof ing === 'object' ? ing._id : ing),
        manufacturers: tradename.manufacturers.map(man => typeof man === 'object' ? man._id : man),
        codes: parsedCodes
      };

      let response;
      if (mode === "edit") {
        response = await TradenameAPI.edit(tradename._id, tradenameData);
        toast.success('Tradename updated successfully');
      } else {
        response = await TradenameAPI.create(tradenameData);
        toast.success('Tradename created successfully');
      }

      if (onSuccess) {
        onSuccess(response);
      }

      onClose();
    } catch (error) {
      toast.error(`Failed to ${mode} tradename`);
      setSubmitting(false);
    }
  };

  // Consolidated input handlers
  const handleInputChange = (e, field) => {
    const value = e.target.value;

    if (field === 'name') {
      setTradename(prev => {
        // Only auto-generate URI if in create mode or if URI is empty
        const shouldUpdateUri = mode === "create" || !prev.uri;
        return {
          ...prev,
          name: value,
          ...(shouldUpdateUri ? { uri: generateUri(value) } : {})
        };
      });
    } else if (field === 'uri') {
      if (validateUri(value)) {
        setTradename(prev => ({ ...prev, uri: value }));
      }
    }
    else if (field === 'description') {
      setTradename(prev => ({ ...prev, description: value }));
    }
    else if (field === 'rank') {
      setTradename(prev => ({ ...prev, rank: value }));
    }
    else if (field === 'is_sponsor') {
      setTradename(prev => ({ ...prev, is_sponsor: e.target.checked }));
    }
    else if (field === 'review_score') {
      setTradename(prev => ({ ...prev, review_score: value }));
    }
    else if (field === 'purchase_link') {
      setTradename(prev => ({ ...prev, purchase_link: value }));
    }
    else if (field === 'categories') {
      // split the value by comma and trim the spaces
      const categories = value.split(',').map(cat => cat.trim());
      setTradename(prev => ({ ...prev, categories: categories }));
    }
    else if (field === 'codes') {
      setTradename(prev => ({ ...prev, codes: value }));
    }else if(field === 'status'){
      setTradename(prev => ({ ...prev, status: e.target.checked ? "active" : "inactive" }));
    }
    
  };

  // Methods for managing manufacturers
  const openManufacturerEdit = (uri) => {
    openManufacturerDialog({
      mode: 'edit',
      manufacturerUri: uri,
      onSuccess: (updatedManufacturer) => {
        toast.success(`Manufacturer ${updatedManufacturer.name} updated`);
      }
    });
  };

  const handleRemoveManufacturer = (id) => {
    setTradename(prev => ({
      ...prev,
      manufacturers: prev.manufacturers.filter(m => m._id !== id)
    }));
    toast.info('Manufacturer removed from tradename. Save changes to confirm.');
  };

  const handleAddManufacturer = () => {
    openManufacturerDialog({
      mode: 'create',
      onSuccess: (newManufacturer) => {
        if (newManufacturer) {
          // Format the new manufacturer to match our expected structure
          const formattedManufacturer = {
            _id: newManufacturer._id,
            name: newManufacturer.name,
            uri: newManufacturer.uri
          };

          // Add the new manufacturer to our list
          setTradename(prev => ({
            ...prev,
            manufacturers: [...prev.manufacturers, formattedManufacturer]
          }));

          // Show success message
          toast.success(`Manufacturer ${newManufacturer.name} added`);
        }
      }
    });
  };

  // Methods for managing ingredients
  const openIngredientEdit = (uri) => {
    openIngredientDialog({
      mode: 'edit',
      ingredientUri: uri,
      onSuccess: (updatedIngredient) => {
        toast.success(`Ingredient ${updatedIngredient.name} updated`);
      }
    });
  };

  const handleRemoveIngredient = (id) => {
    setTradename(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter(i => i._id !== id)
    }));
    toast.info('Ingredient removed from tradename. Save changes to confirm.');
  };

  const handleAddIngredient = () => {
    openIngredientDialog({
      mode: 'create',
      onSuccess: (newIngredient) => {
        if (newIngredient) {
          // Format the new ingredient to match our expected structure
          const formattedIngredient = {
            _id: newIngredient._id,
            name: newIngredient.name,
            uri: newIngredient.uri
          };

          // Add the new ingredient to our list
          setTradename(prev => ({
            ...prev,
            ingredients: [...prev.ingredients, formattedIngredient]
          }));

          // Show success message
          toast.success(`Ingredient ${newIngredient.name} added`);
        }
      }
    });
  };

  // Update handleSearchIngredients to handle pagination
  const handleSearchIngredients = async (currentPage = 1) => {
    if (!ingredientSearchKeyword) {
      setIngredientSearchResults([]);
      setIngredientPagination(prev => ({ ...prev, totalItems: 0 }));
      return;
    }
    
    try {
      setLoading(true);
      const response = await IngredientAPI.search(
        ingredientSearchKeyword, 
        currentPage
      );
      setIngredientSearchResults(response.results || []);
      setIngredientPagination(prev => ({
        ...prev,
        totalItems: response.pagination.totalItems || 0
      }));
    } catch (error) {
      console.error('Error searching ingredients:', error);
      toast.error('Failed to search ingredients');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExistingIngredient = (ingredient) => {
    // Check if this ingredient is already in the list
    const exists = tradename.ingredients.some(i => i._id === ingredient._id);
    if (!exists) {
      setTradename(prev => ({
        ...prev,
        ingredients: [...prev.ingredients, {
          _id: ingredient._id,
          name: ingredient.name,
          uri: ingredient.uri
        }]
      }));
      toast.success(`Added ${ingredient.name} to this tradename`);
    } else {
      toast.info(`${ingredient.name} is already added to this tradename`);
    }
    
    // Clear search results and search keyword after adding
    setIngredientSearchResults([]);
    setIngredientSearchKeyword('');
  };

  // Update handleSearchManufacturers to handle pagination
  const handleSearchManufacturers = async (currentPage = 1) => {
    if (!manufacturerSearchKeyword) {
      setManufacturerSearchResults([]);
      setManufacturerPagination(prev => ({ ...prev, totalItems: 0 }));
      return;
    }
      
    try {
      setLoading(true);
      console.log(currentPage);
      const response = await ManufacturerAPI.search(
        manufacturerSearchKeyword, 
        currentPage
      );
      setManufacturerSearchResults(response.results || []);
      setManufacturerPagination(prev => ({
        ...prev,
        totalItems: response.pagination.totalItems || 0
      }));
    } catch (error) {
      console.error('Error searching manufacturers:', error);
      toast.error('Failed to search manufacturers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExistingManufacturer = (manufacturer) => {
    // Check if this manufacturer is already in the list
    const exists = tradename.manufacturers.some(m => m._id === manufacturer._id);
    if (!exists) {
      setTradename(prev => ({
        ...prev,
        manufacturers: [...prev.manufacturers, {
          _id: manufacturer._id,
          name: manufacturer.name,
          uri: manufacturer.uri
        }]
      }));
      toast.success(`Added ${manufacturer.name} to this tradename`);
    } else {
      toast.info(`${manufacturer.name} is already added to this tradename`);
    }
    
    // Clear search results and search keyword after adding
    setManufacturerSearchResults([]);
    setManufacturerSearchKeyword('');
  };

  // Add handlers for pagination changes
  const handleIngredientPageChange = (page) => {
    setIngredientPagination(prev => ({ ...prev, page: page }));
    handleSearchIngredients(page);
  };

  const handleManufacturerPageChange = (page) => {
    setManufacturerPagination(prev => ({ ...prev, page: page }));
    handleSearchManufacturers(page);
  };

  return (
    <Dialog
      open={isOpen}
      handler={onClose}
      size="lg"
    >
      <Dialog.Overlay>
        <Dialog.Content>
          <div className="p-0">
            <div className="flex items-center justify-between p-4 border-b">
              <Typography variant="h5">
                {mode === "create" ? "Create New Tradename" : `Edit ${tradename.name || 'Tradename'}`}
              </Typography>
              <IconButton onClick={onClose} size="sm">
                <HiX className="w-5 h-5" />
              </IconButton>
            </div>

            <div className="p-4 overflow-y-auto max-h-[calc(100vh-200px)]">
              <div className="grid gap-4">
                {/* Main Information */}
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <div className="mb-2 font-medium text-gray-700">Name *</div>
                    <Input
                      type="text"
                      value={tradename.name}
                      onChange={(e) => handleInputChange(e, 'name')}
                      required
                    />
                  </div>
                  <div>
                    <div className="mb-2 font-medium text-gray-700">URI *</div>
                    <Input
                      type="text"
                      value={tradename.uri}
                      onChange={(e) => handleInputChange(e, 'uri')}
                      required
                      disabled={mode === "edit"}
                    />
                  </div>
                </div>
                {/* Additional Information ['description', 'rank', 'is_sponsor', 'review_score' , 'purchase_link']*/}
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <div className="mb-2 font-medium text-gray-700">Description</div>
                    <Input
                      type="text"
                      value={tradename.description}
                      maxLength={350}
                      onChange={(e) => handleInputChange(e, 'description')}
                    />
                   <HelperInput helperText="Limit to 350 characters" />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <div className="mb-2 font-medium inline-block text-transparent bg-gradient-title bg-clip-text">Enable Sponsor/Affiliate Badge</div>
                    <div className="flex items-center pt-1">
                      <Checkbox defaultChecked={tradename.is_sponsor} id="is_sponsor" onChange={(e) => handleInputChange(e, 'is_sponsor')}>
                        <Checkbox.Indicator />
                      </Checkbox>
                      <Typography
                        as="label"
                        htmlFor="is_sponsor"
                        className="cursor-pointer text-foreground text-gray-700 ml-2"
                      >
                        Is Sponsor
                      </Typography>
                    </div>
                    <HelperInput helperText="First priority on display on the event page"  hideIcon/>
                  </div>
                  <div>
                    <div className="mb-2 font-medium text-gray-700">Rank</div>
                    <Input
                      type="number"
                      value={tradename.rank}
                      min={1}
                      onChange={(e) => handleInputChange(e, 'rank')}
                    />
                    <HelperInput helperText="Rank 1 is the highest rank. 100 is the lowest rank." />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <div className="mb-2 font-medium text-gray-700">Review Score</div>
                    <Input
                      type="number"
                      value={tradename.review_score}
                      format="number"
                      min={1}
                      max={5}
                      onChange={(e) => handleInputChange(e, 'review_score')}
                    />
                    <HelperInput helperText="5 is the highest score. 1 is the lowest score." />
                  </div>
                  <div>
                    <div className="mb-2 font-medium text-gray-700">Purchase Link</div>
                    <Input
                      type="text"
                      value={tradename.purchase_link}
                      onChange={(e) => handleInputChange(e, 'purchase_link')}
                    />
                    <HelperInput helperText="Link to the product page or purchase page." />
                  </div>
                </div>
                {/* Classification Section for [categories, codes, tags] */}
                <div className="flex items-center justify-between mb-2">
                  <Typography className="font-semibold">
                    Classification
                  </Typography>
                </div>

                <div className="p-3 rounded-md bg-gray-50">
                  <div className="mb-3">
                    <div className="mb-2 font-medium text-gray-700">Categories ({tradename.categories?.length > 0 ? tradename.categories.length : '0'})</div>
                    <Input
                      type="text"
                      value={tradename.categories}
                      onChange={(e) => handleInputChange(e, 'categories')}
                    />
                    <HelperInput helperText="Use comma (,) to separate categories" />
                  </div>
                  <div>
                    <div className="mb-2 font-medium text-gray-700">Codes</div>
                    <Textarea
                      value={tradename.codes}
                      onChange={(e) => handleInputChange(e, 'codes')}
                      disabled={loading}
                      placeholder="ATC: A01BC23"
                      rows={4}
                    />
                    <HelperInput helperText={`Enter one code per line in format: "Type: Value" (e.g., "ATC: A01BC23")`} hideIcon />
                  </div>
                </div>
                {/* Ingredients Section */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <Typography className="font-semibold">
                      Ingredients {tradename.ingredients && tradename.ingredients.length > 0 && `(${tradename.ingredients.length})`}
                    </Typography>
                  </div>
                  
                  <div className="p-3 rounded-md bg-gray-50">
                    {/* Search for existing ingredients */}
                    <QuickSearchAndResult
                      searchKeyword={ingredientSearchKeyword}
                      onSearchKeywordChange={setIngredientSearchKeyword}
                      onSearch={() => handleSearchIngredients(ingredientPagination.page)}
                      searchResults={ingredientSearchResults}
                      onAddItem={handleAddExistingIngredient}
                      title="Search Existing Ingredients"
                      resultTitle="Search Results"
                      currentPage={ingredientPagination.page}
                      pageSize={ingredientPagination.limit}
                      totalItems={ingredientPagination.totalItems}
                      onPageChange={handleIngredientPageChange}
                      loading={loading}
                    />
                    
                    {/* Added ingredients */}
                    {tradename.ingredients && tradename.ingredients.length > 0 ? (
                      <div className="mb-3">
                        <div className="mb-2 font-medium text-gray-700">Added Ingredients ({tradename.ingredients.length})</div>
                        <div className="max-h-[200px] overflow-y-auto">
                          {tradename.ingredients.map((ingredient) => (
                            <div 
                              key={ingredient._id} 
                              className="flex items-center justify-between p-2 mb-2 bg-white border border-gray-200 rounded hover:bg-blue-50"
                            >
                              <div className="flex items-center">
                                <HiTag className="mr-2 text-green-500" />
                                <span>{ingredient.name}</span>
                              </div>
                              <div className="flex gap-2">
                                <IconButton
                                  size="sm"
                                  onClick={() => openIngredientEdit(ingredient.uri)}
                                  className="p-1"
                                >
                                  <HiPencil className="text-blue-500" />
                                </IconButton>
                                <IconButton
                                  size="sm"
                                  onClick={() => handleRemoveIngredient(ingredient._id)}
                                  className="p-1"
                                >
                                  <HiX className="text-red-500" />
                                </IconButton>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="py-4 text-center text-gray-500">
                        {loading ? (
                          <div className="flex justify-center">
                            <Spinner size={6} />
                          </div>
                        ) : (
                          <>
                            <HiInformationCircle className="mx-auto mb-2 text-xl text-blue-400" />
                            <p>{mode === 'edit' ? "No ingredients associated with this tradename yet." : "No ingredients added yet."}</p>
                          </>
                        )}
                      </div>
                    )}
                    
                    <div className="flex justify-center mt-3">
                      <Button
                        onClick={handleAddIngredient}
                        className="flex items-center gap-2"
                      >
                        <HiPlus className="w-3 h-3" /> Create New Ingredient
                      </Button>
                    </div>
                  </div>
                  
                  <Typography className="block mt-1 text-gray-500">
                    Note: Changes to ingredient associations will be saved when you click "Save Changes".
                  </Typography>
                </div>

                {/* Manufacturers Section */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <Typography className="font-semibold">
                      Manufacturers {tradename.manufacturers && tradename.manufacturers.length > 0 && `(${tradename.manufacturers.length})`}
                    </Typography>
                  </div>
                  
                  <div className="p-3 rounded-md bg-gray-50">
                    {/* Search for existing manufacturers */}
                    <QuickSearchAndResult
                      searchKeyword={manufacturerSearchKeyword}
                      onSearchKeywordChange={setManufacturerSearchKeyword}
                      onSearch={() => handleSearchManufacturers(manufacturerPagination.page)}
                      searchResults={manufacturerSearchResults}
                      onAddItem={handleAddExistingManufacturer}
                      title="Search Existing Manufacturers"
                      resultTitle="Search Results"
                      currentPage={manufacturerPagination.page}
                      pageSize={manufacturerPagination.limit}
                      totalItems={manufacturerPagination.totalItems}
                      onPageChange={handleManufacturerPageChange}
                      loading={loading}
                    />
                    
                    {/* Added manufacturers */}
                    {tradename.manufacturers && tradename.manufacturers.length > 0 ? (
                      <div className="mb-3">
                        <div className="mb-2 font-medium text-gray-700">Added Manufacturers ({tradename.manufacturers.length})</div>
                        <div className="max-h-[200px] overflow-y-auto">
                          {tradename.manufacturers.map((manufacturer) => (
                            <div 
                              key={manufacturer._id} 
                              className="flex items-center justify-between p-2 mb-2 bg-white border border-gray-200 rounded hover:bg-blue-50"
                            >
                              <div className="flex items-center">
                                <HiTag className="mr-2 text-blue-500" />
                                <span>{manufacturer.name}</span>
                              </div>
                              <div className="flex gap-2">
                                <IconButton
                                  size="sm"
                                  onClick={() => openManufacturerEdit(manufacturer.uri)}
                                  className="p-1"
                                >
                                  <HiPencil className="text-blue-500" />
                                </IconButton>
                                <IconButton
                                  size="sm"
                                  onClick={() => handleRemoveManufacturer(manufacturer._id)}
                                  className="p-1"
                                >
                                  <HiX className="text-red-500" />
                                </IconButton>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="py-4 text-center text-gray-500">
                        {loading ? (
                          <div className="flex justify-center">
                            <Spinner size={6} />
                          </div>
                        ) : (
                          <>
                            <HiInformationCircle className="mx-auto mb-2 text-xl text-blue-400" />
                            <p>{mode === 'edit' ? "No manufacturers associated with this tradename yet." : "No manufacturers added yet."}</p>
                          </>
                        )}
                      </div>
                    )}
                    
                    <div className="flex justify-center mt-3">
                      <Button
                        onClick={handleAddManufacturer}
                        className="flex items-center gap-2"
                      >
                        <HiPlus className="w-3 h-3" /> Create New Manufacturer
                      </Button>
                    </div>
                  </div>
                  
                  <Typography className="block mt-1 text-gray-500">
                    Note: Changes to manufacturer associations will be saved when you click "Save Changes".
                  </Typography>
                </div>
              </div>
            </div>

            <div className={`flex ${mode === "edit" ? "justify-between" : "justify-end"} gap-4 p-4 border-t`}>
             {mode === "edit" && (<div className="flex items-center gap-2">
                <Switch id="switch" color="success" checked={tradename.status === "active"} onChange={(e) => handleInputChange(e, 'status')} />
                <Typography
                  as="label"
                  htmlFor="switch"
                  className="cursor-pointer text-foreground"
                >
                  Publish Status
                </Typography>
              </div>)
              }
              <div className="flex items-center gap-2">
                <Button
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={loading || submitting || !tradename.name || !tradename.uri}
                  className="flex items-center gap-2"
                >
                  {submitting ?
                    <span className="flex items-center gap-2">
                      <Spinner size={4} />
                      Saving...
                    </span> :
                    <>
                      <HiSave className="w-4 h-4" />
                      {mode === "create" ? "Create Tradename" : "Save Changes"}
                    </>
                  }
                </Button>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
};

export default TradenameDialog; 